import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import ProgressSpinner from '../../material/ProgressSpinner';
import Actions from '../Controls/Actions';
import ActionBar from './ActionBar';
import SelectedItemsRibbon from './SelectedItemsRibbon';
import { formatClassName } from '../../Util';
import i18n from '../../../src/i18n/translate';
import { KebabCase } from '../../util/StringUtil';

/** renders data inside of a BootstrapTable component */
export default class Table extends React.Component {
  static propTypes = {
    /* if present passed to actionBar component */
    actionBar: PropTypes.array,
    actions: PropTypes.arrayOf(PropTypes.shape({
      action: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      actionType: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      download: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      icon: PropTypes.string,
      target: PropTypes.string,
      title: PropTypes.string,
      tooltip: PropTypes.string,
      type: PropTypes.oneOf(['edit', 'delete'])
    })).isRequired,
    actionsWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    classes: PropTypes.string,
    /** array of data that will be rendered in he table */
    data: PropTypes.array.isRequired,
    dataObjectType: PropTypes.string,
    /** define how the table rows are rendered
     *  (passed to ResponsiveDataList -> Table -> BootstrapTable component)
     *  documentation of additional BootstrapTable props can be found at
     *  https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/column-props.html */
    dropdownActions: PropTypes.bool,
    fields: PropTypes.arrayOf(PropTypes.shape({
      /** set to true for an action column to add action styles */
      actionColumn: PropTypes.bool,
      /** set to true make the column sortable */
      canSort: PropTypes.bool,
      /** adds additional classes to the renered cells */
      className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func
      ]),
      /** don't really need this anymore for this version of BootstrapTable */
      columnOptions: PropTypes.object,
      /** overrides what is rendered as cell content */
      formatter: PropTypes.func,
      /** renders this row attribute as cell text */
      name: PropTypes.string,
      /** defines the column header */
      title: PropTypes.string,
      /** hard code the styled width of a column in either px or % */
      width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ])
    })).isRequired,
    /** specifies what row attribute is used as the key for each rendered row */
    keyField: PropTypes.string.isRequired,
    options: PropTypes.shape({
      /** sets a message to be displayed when the data array is empty */
      noDataText: PropTypes.string,
      /** callback(event, row) when a row is clicked
       *  note: when there is an onRowClick prop, row hover style is applied */
      onRowClick: PropTypes.func,
      /** callback(sortColumn, sortDirection) when sort is changed */
      onSortChange: PropTypes.func,
      /** column name to sort by */
      sortName: PropTypes.string,
      /** order to sort by */
      sortOrder: PropTypes.oneOf(['asc', 'desc']),
      /** DEPRECATED OPTINOS */
      defaultSortName: PropTypes.stirng,
      defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
      defaultSorted: PropTypes.arrayOf(
        PropTypes.shape({
          dataField: PropTypes.string,
          order: PropTypes.oneOf(['asc', 'desc'])
        })
      ),
      noDataIndication: PropTypes.string,
      rowEvents: PropTypes.object
    }),
    selectRow: PropTypes.object,
    /** passed to SelectedItemsRibbon */
    selectedItems: PropTypes.shape({
      count: PropTypes.number
    }),
    /** set to true when you want to display a loading icon */
    isLoadingData: PropTypes.bool,
    /** add additional classes to the rendered rows */
    trClassName: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func
    ]),
    /** adds a caption above the rendered table */
    tableCaption: PropTypes.string,
    /** adds a className to the div that wraps the BootstrapTable component */
    tableContainerClass: PropTypes.string
  };

  static defaultProps = {
    allowEdit: false
  };

  constructor(props) {
    super(props);

    this.state = {
      highlightRow: null
    };
  }

  componentWillUnmount() {
    this.unhighlightRow();
  }

  highlightRow = value => {
    this.unhighlightRow();
    this.setState({ highlightRow: value });
    this.timeout = setTimeout(this.unhighlightRow, 3000);
  };

  unhighlightRow = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.setState({ highlightRow: null });
  };

  trClassName = (row, rowIndex) => {
    let classNames = [];
    if (this.state.highlightRow) {
      if (row[this.props.keyField] === this.state.highlightRow) {
        classNames.push("highlighted");
      }
    }
    switch (typeof this.props.trClassName) {
    case "function":
      classNames.push(this.props.trClassName(row, rowIndex));
      break;
    case "string":
      classNames.push(this.props.trClassName);
      break;
    }
    return classNames.join(" ");
  };

  renderColumnTitle(column, colIndex, { sortElement }) {
    return (
      <>
        <span className="th-title">{column.text}</span>
        { sortElement }
      </>
    );
  }

  renderCaption = () => {
    if(!this.props.tableCaption) {
      return null;
    }
    return (
      <div className="sr-only">{this.props.tableCaption}</div>
    );
  };

  getColumnWidth = (field) => {
    let widthUnits = ['%', 'px'];
    let width = field.columnOptions && field.columnOptions.width ? field.columnOptions.width : field.width;
    if(width &&  (typeof width === 'number' || !widthUnits.some((unit)=> width.includes(unit)))) {
      width = `${width}px`;
    }
    return {
      width: width,
      'min-width': width  // for IE11
    }
  }

  stopOverlayPropagation = (e) => {
    e.stopPropagation();
  }

  resetSortText = () => {
    this.props.fields.map((field) => {
      let attrTxt = i18n.t('%s sortable', { postProcess: 'sprintf', sprintf: [field.title] });
      if(this.props.options && field.name === this.props.options.defaultSortName && this.props.options.defaultSortOrder){
        const dir = this.props.options.defaultSortOrder === 'asc' ? i18n.t('ascending') : i18n.t('descending');
        i18n.t('%s default sortable %s', { postProcess: 'sprintf', sprintf: [field.title, dir] });
      }
      field.headerAttrs = field.sort ?  { 'aria-label': attrTxt, 'data-object-type': this.props.dataObjectType, 'data-object-action': `sort-${KebabCase(field.name)}` } : {};
    });
  }

  findColumnByTitle = (name) => {
    return this.props.fields.find((field) => field.name === name);
  }

  alias = (object, newProp, oldProp) => {
    if (!object[newProp] && !!object[oldProp]) {
      object[newProp] = object[oldProp];
    }
  }

  formatColumns = () => {
    const { actions, actionsWidth, dropdownActions, keyField } = this.props;
    const fields = this.props.fields.concat();

    if (actions && actions.length) {
      const defaultActionsWidth = actions.length === 4 ?
        136 : 110;
      fields.push({
        name: 'actions',
        title: i18n.t('Actions'),
        canSort: false,
        actionColumn: true,
        width: actionsWidth || defaultActionsWidth,
        formatter: (_, row) => (
          <Actions
            actions={actions}
            dropdownActions={dropdownActions}
            keyField={keyField}
            row={row} />
        )
      });
    }

    return fields.map((field) => {
      this.alias(field, 'text', 'title');
      this.alias(field, 'dataField', 'name');
      this.alias(field, 'sort', 'canSort');
      field.headerFormatter = this.renderColumnTitle;
      this.resetSortText();
      if(field.sort){
        field.onSort = (name, ord) => {
          const dir = (ord === 'asc') ? i18n.t('ascending') : i18n.t('descending');
          const fld = this.findColumnByTitle(name);
          this.resetSortText();
          fld.headerAttrs = { 'aria-label': i18n.t('%s sort %s', { postProcess: 'sprintf', sprintf: [fld.text, dir] }), 'aria-sort': i18n.t(dir) };
          if(this.props.options && this.props.options.onSortChange){
            this.props.options.onSortChange(name, ord);
          }
        }
      }

      field.headerStyle = field.style = this.getColumnWidth(field);
      field.align = !field.align && field.columnOptions && field.columnOptions.dataAlign ? field.columnOptions.dataAlign : 'left';
      this.alias(field, 'headerAlign', 'align');
      this.alias(field, 'classes', 'className');
      if (field.actionColumn) {
        field.classes = field.classes ? "action-column " + field.classes : "action-column";
      }

      return field;
    });
  }


  handleUserClickEvent = (e, row) => {
    let selectionType = window.getSelection().type;
    if(selectionType !== "Range") {
      if(this.props.options.onRowClick) {
        this.props.options.onRowClick(e, row);
      }
    }
  }

  customMultiSelectHeader = ({ indeterminate, ...rest }) => {
    return (
      <div className="checkbox-personalized">
        <input
          type="checkbox"
          ref={(input) => {
            if (input) input.indeterminate = indeterminate;
          }}
          {...rest}
          disabled={this.props.isLoadingData || rest.disabled}
        />
        <label htmlFor={'checkbox' + rest.rowIndex}>
          <div className="check" />
        </label>
      </div>
    )
  }

  customMultiSelect = ({ mode, ...rest }) => {
    return (
      <div className="checkbox-personalized">
        <input type={mode} {...rest} />
        <label htmlFor={'checkbox' + rest.rowIndex}>
          <div className="check" />
        </label>
      </div>
    );
  }

  get selectRow() {
    if (!this.props.selectRow) {
      return;
    }
    return {
      selectionRenderer: this.customMultiSelect,
      selectionHeaderRenderer: this.customMultiSelectHeader,
      ...this.props.selectRow
    };
  }

  renderTable(enableHover) {
    const { actionBar, classes, data, keyField, options, selectedItems, tableContainerClass } = this.props;
    let columns = this.formatColumns();
    let defaultSorted = [];
    let rowEvents = {};
    let containerClassName = tableContainerClass ? `react-bootstrap-table-container ${tableContainerClass}` : 'react-bootstrap-table-container';
    let noDataIndication = () => {};

    if(options){
      if(!options.defaultSorted && !!options.sortName && !!options.sortOrder) {
        defaultSorted.push({
          dataField: options.sortName,
          order: options.sortOrder
        });
      } else {
        defaultSorted = options.defaultSorted;
      }

      if(!options.rowEvents && options.onRowClick) {
        rowEvents.onClick = this.handleUserClickEvent;
      } else {
        rowEvents = options.rowEvents;
      }

      if(!options.noDataIndication && options.noDataText){
        noDataIndication = () => { return  options.noDataText };
      } else {
        noDataIndication = options.noDataIndication;
      }
    }

    const selectRow = this.selectRow;
    const className = formatClassName([
      classes,
      { 'custom-select-class': !!this.selectRow }
    ]);



    return (
      <div className={containerClassName}>
        {<SelectedItemsRibbon {...selectedItems} />}
        {actionBar && selectedItems && <ActionBar buttons={actionBar} disabled={selectedItems.count === 0} />}
        <BootstrapTable
          data={data}
          columns={columns}
          keyField={keyField}
          defaultSorted={defaultSorted}
          caption={this.renderCaption()}
          bordered={false}
          hover={enableHover}
          {...this.props}
          classes={className}
          selectRow={selectRow}
          rowClasses={this.trClassName}
          keyBoardNav= {{ clickToNav: false }}
          rowEvents={rowEvents}
          noDataIndication={noDataIndication}
        />
      </div>
    );
  }

  render() {
    const { isLoadingData, options } = this.props
    let enableHover = options && options.onRowClick;
    let loading = isLoadingData === true;
    return (
      <div className={loading ? 'table-data-loading' : undefined}>
        {loading && <ProgressSpinner containerClassName="everfi-list-loader"/>}
        {this.renderTable(enableHover)}
      </div>
    )
  }
}
